import {SurveyStatus} from 'crqlar-react-common/dist/models/survey/survey.model';
import {useTranslation} from 'react-i18next';

import {useGuestCategories} from '../../../../../guestCategories/useGuestCategories';
import {
    isRemindableSurveyType,
    readGuestCategoryFilter,
    readSurveyFilter,
    surveyTypeLabel,
    writeGuestCategoryFilter,
    writeSurveyFilter,
    writeSurveyTriggerConfig
} from '../../../config/surveyTriggers';
import {MultiSelect} from '../../../../../common/newSelect';
import type {SelectOption} from '../../../../../common/newSelect';
import {AutomationSurveyOption, NodeEditorV2Props} from '../types';

interface SurveyTriggerFiltersProps {
    nodeConfig: Record<string, any>;
    setNodeConfig: NodeEditorV2Props['setNodeConfig'];
    availableSurveys?: AutomationSurveyOption[];
    /** True while surveys load — nothing reads as gone yet. */
    availableSurveysLoading?: boolean;
    /** Hide anonymous surveys: with no invitation and no guest, a trigger on them can never fire. */
    excludeAnonymous?: boolean;
    readOnly?: boolean;
}

/**
 * The filters both survey triggers share: surveys (≥1 required, grouped by type, drafts labelled) and the guest
 * categories snapshotted on the invitation. Ids go through the `config/surveyTriggers` helpers.
 * No "Any survey", anonymous switch or response filter here (Continue if does that).
 */
const SurveyTriggerFilters = ({
    nodeConfig,
    setNodeConfig,
    availableSurveys = [],
    availableSurveysLoading,
    excludeAnonymous,
    readOnly
}: SurveyTriggerFiltersProps) => {
    const {t} = useTranslation();
    const {categories, isLoading: categoriesLoading} = useGuestCategories();

    const surveyFilter = readSurveyFilter(nodeConfig);
    const guestCategoryFilter = readGuestCategoryFilter(nodeConfig);

    const update = (patch: Record<string, any>) => setNodeConfig(prev => writeSurveyTriggerConfig(prev, patch));

    // The select keeps the order it is handed: survey types A to Z, surveys A to Z inside them.
    const surveyOptions: SelectOption[] = availableSurveys
        .filter(survey => !excludeAnonymous || isRemindableSurveyType(survey.type))
        .map(survey => ({
            value: survey.id,
            label: survey.status === SurveyStatus.PUBLISHED
                ? (survey.name || '')
                : `${survey.name || ''} ${t('(draft)')}`.trim(),
            group: survey.type ? surveyTypeLabel(survey.type, t) : t('Other')
        }))
        .sort((left, right) => left.group.localeCompare(right.group) || left.label.localeCompare(right.label));

    const guestCategoryOptions: SelectOption[] = categories.map(category => ({
        value: category.id!,
        label: category.name || ''
    }));

    // Keep the field in place while loading and when empty. Stored selections remain removable.
    const noGuestCategories = !categoriesLoading && !categories.length && !guestCategoryFilter.ids.length;

    const handleSurveysChange = (ids: string[]) => {
        const selection = ids.map(id => ({
            id,
            name: availableSurveys.find(survey => survey.id === id)?.name || surveyFilter.names[id]
        }));

        update(writeSurveyFilter(selection));
    };

    const handleGuestCategoriesChange = (ids: string[]) => {
        const selection = ids.map(id => ({
            id,
            name: categories.find(category => category.id === id)?.name || guestCategoryFilter.names[id]
        }));

        update(writeGuestCategoryFilter(selection));
    };

    return (
        <>
            {/* `loading` stays undefined once settled: only a non-empty list proves a stored pick is gone. */}
            <MultiSelect
                label={t('Survey')}
                placeholder={t('Pick at least one survey')}
                value={surveyFilter.ids}
                options={surveyOptions}
                unknownLabels={surveyFilter.names}
                onChange={handleSurveysChange}
                loading={availableSurveysLoading || undefined}
                disabled={readOnly}
            />

            <MultiSelect
                label={t('Only these guest categories')}
                placeholder={t(noGuestCategories ? 'No guest categories available' : 'Any guest category')}
                value={guestCategoryFilter.ids}
                options={guestCategoryOptions}
                unknownLabels={guestCategoryFilter.names}
                onChange={handleGuestCategoriesChange}
                loading={categoriesLoading || undefined}
                disabled={readOnly || noGuestCategories}
            />
        </>
    );
};

export default SurveyTriggerFilters;
